import { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { User, Phone, Shield, Plus, Pencil, Trash2, Users, ChevronDown, ChevronRight, ArrowRight, KeyRound, Eye, EyeOff, UserCheck, Palmtree, AlertTriangle, LogOut, Cake } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { ExitEmployeeDialog } from '../components/ExitEmployeeDialog';

const roleConfig = {
  super_admin: { label: 'Super Admin', color: 'bg-gradient-to-r from-red-500 to-orange-500 text-white' },
  admin: { label: 'Admin', color: 'bg-gradient-to-r from-[#5B6FD6] to-[#4338CA] text-white' },
  member: { label: 'Member', color: 'bg-[#10B981]/10 text-[#10B981]' },
};

export default function Team() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [expandedAdmins, setExpandedAdmins] = useState(new Set());
  const [passwordData, setPasswordData] = useState({});
  const [showPasswords, setShowPasswords] = useState(false);
  const [resetPwdUserId, setResetPwdUserId] = useState(null);
  const [resetPwdValue, setResetPwdValue] = useState('');
  const [wipeDialogOpen, setWipeDialogOpen] = useState(false);
  const [wipeConfirmText, setWipeConfirmText] = useState('');
  const [wiping, setWiping] = useState(false);
  const [exitDialogUser, setExitDialogUser] = useState(null);
  const [loginStatusMap, setLoginStatusMap] = useState({}); // { userId: { logged_in_today, days_inactive, last_login_at } }
  // Pending password-reset requests (super admin only)
  const [resetRequests, setResetRequests] = useState([]);
  const [approveTarget, setApproveTarget] = useState(null); // { id, user_name, user_mobile }
  const [approveNewPwd, setApproveNewPwd] = useState('');
  const [formData, setFormData] = useState({
    full_name: '',
    mobile_number: '',
    password: '',
    role: 'member',
    date_of_birth: '',
    joining_date: '',
    weekly_capacity_hours: '40',
  });

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => { fetchUsers(); fetchResetRequests(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh live login status every 20s for super admins
  useEffect(() => {
    if (currentUser.role !== 'super_admin') return;
    const refresh = async () => {
      try {
        const lsRes = await api.get('/users/login-status');
        const lsMap = {};
        lsRes.data.forEach(u => { lsMap[u.id] = u; });
        setLoginStatusMap(lsMap);
      } catch { /* silent */ }
    };
    const i = setInterval(refresh, 20000);
    return () => clearInterval(i);
  }, [currentUser.role]);

  // Refresh pending reset requests on WS notification
  useEffect(() => {
    if (currentUser.role !== 'super_admin') return;
    const handler = (e) => {
      if (e?.detail?.type === 'password_reset_request') fetchResetRequests();
    };
    window.addEventListener('ws-notification', handler);
    return () => window.removeEventListener('ws-notification', handler);
  }, [currentUser.role]);

  const fetchResetRequests = async () => {
    if (currentUser.role !== 'super_admin') return;
    try {
      const r = await api.get('/auth/forgot-password/pending');
      setResetRequests(r.data || []);
    } catch { /* silent */ }
  };

  const handleApproveReset = async () => {
    if (!approveTarget || !approveNewPwd || approveNewPwd.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.post(`/auth/forgot-password/${approveTarget.id}/approve`, { new_password: approveNewPwd });
      toast.success(`Reset approved for ${approveTarget.user_name}. Share the new password with the user.`);
      setApproveTarget(null); setApproveNewPwd('');
      fetchResetRequests();
      fetchUsers(); // refresh passwords map so the temp pwd shows in the table
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectReset = async (req) => {
    try {
      await api.post(`/auth/forgot-password/${req.id}/reject`);
      toast.success(`Request from ${req.user_name} rejected`);
      fetchResetRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
      const adminIds = new Set(response.data.filter(u => u.role === 'admin').map(u => u.id));
      setExpandedAdmins(adminIds);
      // Fetch passwords for super admin
      if (currentUser.role === 'super_admin') {
        try {
          const pwdRes = await api.get('/auth/passwords');
          const map = {};
          pwdRes.data.forEach(u => {
            map[u.id] = u.last_reset_password || '(not set)';
          });
          setPasswordData(map);
        } catch (err) { console.warn('Password fetch failed:', err); }
        // Fetch login status
        try {
          const lsRes = await api.get('/users/login-status');
          const lsMap = {};
          lsRes.data.forEach(u => { lsMap[u.id] = u; });
          setLoginStatusMap(lsMap);
        } catch (err) { console.warn('Login status fetch failed:', err); }
      }
    } catch (error) {
      toast.error('Failed to load team members');
    } finally { setLoading(false); }
  };

  const resetForm = () => {
    setFormData({ full_name: '', mobile_number: '', password: '', role: 'member', date_of_birth: '', joining_date: '', weekly_capacity_hours: '40' });
    setIsEditMode(false);
    setEditingUserId(null);
  };

  const handleResetPassword = async () => {
    if (!resetPwdUserId || !resetPwdValue) return;
    try {
      await api.put(`/auth/reset-password/${resetPwdUserId}`, { new_password: resetPwdValue });
      toast.success('Password reset successfully');
      setResetPwdUserId(null); setResetPwdValue('');
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to reset password'); }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!formData.full_name || !formData.mobile_number) { toast.error('Please fill all required fields'); return; }
    if (!isEditMode && !formData.password) { toast.error('Password is required for new users'); return; }

    // Validate mobile number (Indian 10-digit, starts with 6-9)
    const digits = String(formData.mobile_number || '').replace(/\D/g, '');
    const normalizedMobile = digits.startsWith('91') && digits.length === 12
      ? digits.slice(2)
      : digits.startsWith('0') && digits.length === 11 ? digits.slice(1) : digits;
    if (!/^[6-9]\d{9}$/.test(normalizedMobile)) {
      toast.error('Enter a valid 10-digit mobile number starting with 6, 7, 8 or 9.');
      return;
    }

    try {
      if (isEditMode) {
        await api.put(`/users/${editingUserId}`, {
          full_name: formData.full_name, role: formData.role, mobile_number: normalizedMobile,
          date_of_birth: formData.date_of_birth || null,
          joining_date: formData.joining_date || null,
          weekly_capacity_hours: formData.weekly_capacity_hours ? parseFloat(formData.weekly_capacity_hours) : null,
        });
        toast.success('User updated successfully');
      } else {
        await api.post('/auth/register', { ...formData, mobile_number: normalizedMobile });
        toast.success('User created. Pending Super Admin approval to activate.');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} user`); }
  };

  const handleEditUser = (user) => {
    setIsEditMode(true);
    setEditingUserId(user.id);
    setFormData({
      full_name: user.full_name,
      mobile_number: user.mobile_number,
      password: '',
      role: user.role,
      date_of_birth: user.date_of_birth || '',
      joining_date: user.joining_date || '',
      weekly_capacity_hours: (user.weekly_capacity_hours ?? 40).toString(),
    });
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (user) => { setUserToDelete(user); setDeleteDialogOpen(true); };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await api.delete(`/users/${userToDelete.id}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete user'); }
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await api.patch(`/users/${userId}/status`, { is_active: !currentStatus });
      toast.success(`User ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update user status'); }
  };

  const handleAssignAdmin = async (memberId, adminId) => {
    try {
      await api.put(`/users/${memberId}/assign-admin`, { parent_admin_id: adminId || null });
      toast.success('Member assignment updated');
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to assign member'); }
  };

  const handleAssignBackup = async (userId, backupId) => {
    try {
      await api.put(`/users/${userId}/backup`, { backup_employee_id: backupId || null });
      toast.success('Backup employee updated');
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to assign backup'); }
  };

  const handleToggleLeave = async (userId, currentLeaveStatus) => {
    const goOnLeave = !currentLeaveStatus;
    try {
      const res = await api.put(`/users/${userId}/leave-status`, { on_leave: goOnLeave });
      toast.success(res.data.message);
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update leave status'); }
  };

  const toggleExpand = (adminId) => {
    setExpandedAdmins(prev => {
      const next = new Set(prev);
      if (next.has(adminId)) next.delete(adminId); else next.add(adminId);
      return next;
    });
  };

  const handleDialogClose = (open) => { setIsDialogOpen(open); if (!open) resetForm(); };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  if (currentUser.role !== 'super_admin') {
    return (
      <div className="p-8 bg-slate-50 min-h-full">
        <Card className="border-slate-200"><CardContent className="p-12 text-center">
          <Shield className="h-16 w-16 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">Access Denied</p>
          <p className="text-slate-400 text-sm mt-2">Only Super Administrators can view team management</p>
        </CardContent></Card>
      </div>
    );
  }

  // Group data
  const pendingUsers = users.filter(u => u.is_active === false);
  const activeUsers = users.filter(u => u.is_active !== false);
  const superAdmins = activeUsers.filter(u => u.role === 'super_admin');
  const admins = activeUsers.filter(u => u.role === 'admin');
  const members = activeUsers.filter(u => u.role === 'member');
  const adminIdSet = new Set(admins.map(a => a.id));
  const superAdminIdSet = new Set(superAdmins.map(s => s.id));
  // A member is "unassigned" if they have no parent OR their parent no
  // longer exists / is not an active admin or super admin. Members can now
  // report to either tier, so the visibility set is the union.
  const validParentSet = new Set([...adminIdSet, ...superAdminIdSet]);
  const unassignedMembers = members.filter(m => !m.parent_admin_id || !validParentSet.has(m.parent_admin_id));
  const onLeaveCount = activeUsers.filter(u => u.on_leave).length;
  // Users with missing birthday or joining date (active, non-super_admin)
  const missingDatesUsers = activeUsers
    .filter(u => u.role !== 'super_admin' && (!u.date_of_birth || !u.joining_date))
    .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));

  const getMembersForAdmin = (adminId) => members.filter(m => m.parent_admin_id === adminId);
  const getMembersForSuperAdmin = (saId) => members.filter(m => m.parent_admin_id === saId);

  const UserRow = ({ user, isChild = false }) => {
    const role = roleConfig[user.role] || roleConfig.member;
    const isSelf = user.id === currentUser.id;

    return (
      <div className={`flex items-center gap-3 px-5 py-3 ${isChild ? 'pl-10 bg-slate-50/50' : 'bg-white'} hover:bg-slate-50 transition-colors`}
        data-testid={`team-member-${user.id}`}>
        <div className={`${isChild ? 'h-8 w-8' : 'h-10 w-10'} rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-sm`} style={{
          background: user.role === 'super_admin'
            ? 'linear-gradient(135deg, #ef4444, #f97316)'
            : user.role === 'admin'
              ? 'linear-gradient(135deg, #5B6FD6, #4338CA)'
              : 'linear-gradient(135deg, #10B981, #059669)'
        }}>{user.full_name.charAt(0).toUpperCase()}</div>

        <div className="flex-1 min-w-[140px] pr-2">
          <span className="font-semibold text-sm text-slate-900 truncate block">{user.full_name}</span>
          <div className="flex items-center gap-1 mt-0.5 flex-wrap">
            {!user.is_active && <Badge variant="destructive" className="text-[10px] py-0 whitespace-nowrap">Disabled</Badge>}
            {user.on_leave && <Badge className="bg-amber-100 text-amber-700 text-[10px] py-0 border-amber-200 whitespace-nowrap">On Leave</Badge>}
            {currentUser.role === 'super_admin' && user.role !== 'super_admin' && user.is_active && (() => {
              const ls = loginStatusMap[user.id];
              if (!ls) return null;
              if (ls.is_online) {
                return (
                  <Badge className="bg-green-100 text-green-700 text-[10px] py-0 border-green-200 whitespace-nowrap flex items-center gap-1" data-testid={`login-status-${user.id}`}>
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
                    </span>
                    Online
                  </Badge>
                );
              }
              if (ls.logged_in_today) {
                return <Badge className="bg-blue-100 text-blue-700 text-[10px] py-0 border-blue-200 whitespace-nowrap" data-testid={`login-status-${user.id}`}>● Today</Badge>;
              }
              if (ls.last_login_at === null || ls.last_login_at === undefined) {
                return <Badge className="bg-red-100 text-red-700 text-[10px] py-0 border-red-200 whitespace-nowrap" data-testid={`login-status-${user.id}`}>● Never</Badge>;
              }
              const days = ls.days_inactive ?? 0;
              const cls = days >= 3 ? 'bg-red-100 text-red-700 border-red-200' : 'bg-amber-100 text-amber-700 border-amber-200';
              return <Badge className={`${cls} text-[10px] py-0 whitespace-nowrap`} data-testid={`login-status-${user.id}`}>● {days}d ago</Badge>;
            })()}
          </div>
        </div>

        <div className="flex items-center gap-1.5 text-sm text-slate-500 w-36 flex-shrink-0">
          <Phone className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="font-mono tracking-tight">{user.mobile_number}</span>
        </div>

        {currentUser.role === 'super_admin' && (
          <div className="flex items-center gap-1 w-32 flex-shrink-0">
            <span className="text-xs font-mono text-slate-500 bg-slate-100 px-2 py-1 rounded truncate" data-testid={`pwd-${user.id}`}>
              {showPasswords ? (passwordData[user.id] || '(not set)') : '********'}
            </span>
            {user.id !== currentUser.id && (
              <button onClick={() => { setResetPwdUserId(user.id); setResetPwdValue(''); }}
                className="p-1 text-slate-400 hover:text-[#5B6FD6] hover:bg-[#5B6FD6]/10 rounded flex-shrink-0" title="Reset password"
                data-testid={`reset-pwd-${user.id}`}>
                <KeyRound className="h-3 w-3" />
              </button>
            )}
          </div>
        )}

        <Badge className={`${role.color} text-xs w-20 justify-center flex-shrink-0`}>{role.label}</Badge>

        {/* Assign Admin / Super Admin dropdown for members */}
        {user.role === 'member' && (
          <Select value={user.parent_admin_id || 'unassigned'} onValueChange={(v) => handleAssignAdmin(user.id, v === 'unassigned' ? null : v)}>
            <SelectTrigger className="w-32 h-8 text-xs flex-shrink-0" data-testid={`assign-admin-${user.id}`}>
              <SelectValue placeholder="Reports To" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {superAdmins.map(sa => (
                <SelectItem key={sa.id} value={sa.id}>{sa.full_name} (Super Admin)</SelectItem>
              ))}
              {admins.map(a => (
                <SelectItem key={a.id} value={a.id}>{a.full_name} (Admin)</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Backup Employee dropdown for admins and members */}
        {user.role !== 'super_admin' && (
          <Select value={user.backup_employee_id || 'none'} onValueChange={(v) => handleAssignBackup(user.id, v === 'none' ? null : v)}>
            <SelectTrigger className="w-32 h-8 text-xs flex-shrink-0" data-testid={`assign-backup-${user.id}`}>
              <div className="flex items-center gap-1 truncate">
                <UserCheck className="h-3 w-3 flex-shrink-0 text-slate-400" />
                <span className="truncate">{user.backup_employee_id ? (users.find(u => u.id === user.backup_employee_id)?.full_name || 'Unknown') : 'No Backup'}</span>
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Backup</SelectItem>
              {users.filter(u => u.id && u.id !== user.id && u.is_active && !u.on_leave).map(u => (
                <SelectItem key={u.id} value={u.id}>{u.full_name} ({roleConfig[u.role]?.label})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Leave Toggle for admins and members */}
        {!isSelf && user.role !== 'super_admin' && (
          <div className="flex items-center gap-1.5" data-testid={`leave-section-${user.id}`}>
            <Palmtree className={`h-3.5 w-3.5 ${user.on_leave ? 'text-amber-500' : 'text-slate-300'}`} />
            <Switch
              checked={user.on_leave || false}
              onCheckedChange={() => handleToggleLeave(user.id, user.on_leave || false)}
              data-testid={`toggle-leave-${user.id}`}
              className="scale-90"
            />
            {user.on_leave && <Badge className="bg-amber-100 text-amber-700 text-[10px] py-0">On Leave</Badge>}
          </div>
        )}

        {/* Status toggle & actions */}
        {!isSelf && user.role !== 'super_admin' && (
          <div className="flex items-center gap-2">
            <Switch checked={user.is_active ?? true}
              onCheckedChange={() => handleToggleStatus(user.id, user.is_active ?? true)}
              data-testid={`toggle-user-${user.id}`} className="scale-90" />
            <button onClick={() => handleEditUser(user)} className="p-1.5 rounded hover:bg-[#5B6FD6]/10 text-slate-400 hover:text-[#5B6FD6] transition-colors" data-testid={`edit-user-${user.id}`}>
              <Pencil className="h-3.5 w-3.5" />
            </button>
            {currentUser.role === 'super_admin' && (
              <button onClick={() => setExitDialogUser(user)} className="p-1.5 rounded hover:bg-orange-50 text-slate-400 hover:text-orange-600 transition-colors" data-testid={`exit-user-${user.id}`} title="Exit employee (reassign work & deactivate)">
                <LogOut className="h-3.5 w-3.5" />
              </button>
            )}
            <button onClick={() => handleDeleteClick(user)} className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors" data-testid={`delete-user-${user.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        {(isSelf || user.role === 'super_admin') && user.role !== 'member' && <div className="w-[120px]" />}
      </div>
    );
  };

  return (
    <div className="p-8 min-h-full" style={{
        background: 'linear-gradient(135deg, rgba(91, 111, 214, 0.03) 0%, rgba(16, 185, 129, 0.03) 100%)'
      }}>
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight mb-2" style={{
              background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
            }}>Organization</h1>
            <p className="text-lg text-slate-600">Team hierarchy and member management</p>
          </div>

          <div className="flex items-center gap-3">
            {currentUser?.role === 'super_admin' && (
              <Button variant="outline" onClick={() => { setWipeConfirmText(''); setWipeDialogOpen(true); }}
                data-testid="wipe-tasks-btn"
                className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700">
                <AlertTriangle className="h-4 w-4" />
                Wipe All Tasks
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowPasswords(p => !p)} data-testid="toggle-passwords-btn"
              className="flex items-center gap-2">
              {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {showPasswords ? 'Hide' : 'Show'} Passwords
            </Button>
            <Button className="bg-gradient-to-r from-[#5B6FD6] to-[#10B981] hover:from-[#4a5bc5] hover:to-[#0d9668] shadow-md"
              data-testid="add-user-button" onClick={() => setIsDialogOpen(true)}>
              <Plus className="h-5 w-5 mr-2" /> Add Member
            </Button>
          </div>

          {/* Pending Password Reset Requests — super admin only */}
          {currentUser?.role === 'super_admin' && resetRequests.length > 0 && (
            <Card className="border-amber-300 bg-amber-50/40 mb-6" data-testid="reset-requests-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <KeyRound className="h-5 w-5 text-amber-600" />
                  <h2 className="text-lg font-bold text-slate-800">
                    Pending Password Reset Requests
                    <span className="ml-2 text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full" data-testid="reset-requests-count">
                      {resetRequests.length}
                    </span>
                  </h2>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  Users who clicked "Forgot password?" on the login screen. Approve to set a temporary password, then share it with them.
                </p>
                <div className="space-y-2">
                  {resetRequests.map(r => (
                    <div key={r.id} className="flex items-center justify-between bg-white border border-amber-200 rounded-lg px-4 py-2.5"
                      data-testid={`reset-request-${r.id}`}>
                      <div>
                        <div className="font-semibold text-slate-800">{r.user_name} <span className="text-xs text-slate-400 font-normal">({roleConfig[r.user_role]?.label || r.user_role})</span></div>
                        <div className="text-xs text-slate-500">
                          {r.user_mobile} · requested {new Date(r.requested_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => { setApproveTarget(r); setApproveNewPwd(''); }}
                          data-testid={`approve-reset-${r.id}`}
                        >
                          Approve
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleRejectReset(r)}
                          data-testid={`reject-reset-${r.id}`}
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
            <DialogContent className="max-w-md" data-testid="user-dialog">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">{isEditMode ? 'Edit User' : 'Add New Member'}</DialogTitle>
                <DialogDescription className="sr-only">{isEditMode ? 'Edit user details' : 'Add a new team member'}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <Label htmlFor="full_name">Full Name <span className="text-red-500">*</span></Label>
                  <Input id="full_name" data-testid="user-name-input" placeholder="Enter full name"
                    value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} required />
                </div>
                <div>
                  <Label htmlFor="mobile_number">Mobile Number <span className="text-red-500">*</span></Label>
                  <Input id="mobile_number" data-testid="user-mobile-input" placeholder="10-digit mobile (e.g. 9876543210)"
                    inputMode="numeric" pattern="[6-9][0-9]{9}" maxLength={10}
                    value={formData.mobile_number} onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    required />
                  <p className="text-xs text-slate-400 mt-1">10 digits, starting with 6, 7, 8 or 9</p>
                </div>
                {!isEditMode && (
                  <div>
                    <Label htmlFor="password">Password <span className="text-red-500">*</span></Label>
                    <Input id="password" type="password" data-testid="user-password-input" placeholder="Enter password"
                      value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required />
                  </div>
                )}
                <div>
                  <Label htmlFor="role">Role <span className="text-red-500">*</span></Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="member">Member</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="date_of_birth">Date of birth</Label>
                    <Input id="date_of_birth" type="date" data-testid="user-dob-input"
                      value={formData.date_of_birth || ''}
                      onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="joining_date">Work joining date</Label>
                    <Input id="joining_date" type="date" data-testid="user-join-input"
                      value={formData.joining_date || ''}
                      onChange={(e) => setFormData({ ...formData, joining_date: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div>
                    <Label htmlFor="weekly_capacity">Weekly capacity (hours)</Label>
                    <Input id="weekly_capacity" type="number" min="1" max="80" step="0.5" data-testid="user-capacity-input"
                      value={formData.weekly_capacity_hours}
                      onChange={(e) => setFormData({ ...formData, weekly_capacity_hours: e.target.value })}
                      placeholder="40" />
                    <p className="text-[10px] text-slate-400 mt-1">Standard work week = 40h. Adjust for part-time staff.</p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => handleDialogClose(false)}>Cancel</Button>
                  <Button type="submit" className="bg-gradient-to-r from-[#5B6FD6] to-[#10B981]" data-testid="user-submit-button">
                    {isEditMode ? 'Update User' : 'Add Member'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <Card className="border-slate-200"><CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div><p className="text-2xl font-bold text-slate-900">{superAdmins.length}</p><p className="text-xs text-slate-500">Super Admins</p></div>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-[#5B6FD6] to-[#4338CA] flex items-center justify-center">
              <User className="h-5 w-5 text-white" />
            </div>
            <div><p className="text-2xl font-bold text-slate-900">{admins.length}</p><p className="text-xs text-slate-500">Admins</p></div>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center">
              <Users className="h-5 w-5 text-white" />
            </div>
            <div><p className="text-2xl font-bold text-slate-900">{members.length}</p><p className="text-xs text-slate-500">Members</p></div>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
              <Palmtree className="h-5 w-5 text-white" />
            </div>
            <div><p className="text-2xl font-bold text-slate-900" data-testid="on-leave-count">{onLeaveCount}</p><p className="text-xs text-slate-500">On Leave</p></div>
          </CardContent></Card>
        </div>

        {/* Missing Dates Panel */}
        {missingDatesUsers.length > 0 && (
          <div className="mb-6 bg-pink-50/60 border border-pink-200 rounded-xl overflow-hidden" data-testid="missing-dates-section">
            <div className="px-5 py-3 bg-pink-100/60 border-b border-pink-200 flex items-center gap-2">
              <Cake className="h-4 w-4 text-pink-600" />
              <span className="text-sm font-semibold text-pink-800">
                Missing birthday / joining date ({missingDatesUsers.length})
              </span>
              <span className="text-xs text-pink-700/70 ml-1">
                — Ping these folks so celebrations show up for them.
              </span>
            </div>
            <div className="divide-y divide-pink-100 max-h-64 overflow-y-auto">
              {missingDatesUsers.map(u => {
                const missing = [];
                if (!u.date_of_birth) missing.push('birthday');
                if (!u.joining_date) missing.push('joining date');
                const waText = encodeURIComponent(
                  `Hi ${u.full_name?.split(' ')[0] || ''}, please add your ${missing.join(' & ')} in FinTrack Pro (Dashboard → My dates) so the team can wish you. 🎂`
                );
                return (
                  <div key={u.id} className="px-5 py-2.5 flex items-center gap-3 text-sm hover:bg-pink-50/50" data-testid={`missing-dates-row-${u.id}`}>
                    <div className="h-7 w-7 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {u.full_name?.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{u.full_name}</p>
                      <p className="text-xs text-slate-500">
                        <Badge variant="outline" className="text-[10px] mr-2 capitalize">{u.role}</Badge>
                        Missing: {missing.join(', ')}
                      </p>
                    </div>
                    <a href={`tel:${u.mobile_number}`} className="text-xs text-slate-600 hover:text-slate-900 px-2 py-1 rounded hover:bg-white" data-testid={`missing-dates-call-${u.id}`}>
                      {u.mobile_number}
                    </a>
                    <a
                      href={`https://wa.me/91${u.mobile_number}?text=${waText}`}
                      target="_blank" rel="noreferrer"
                      className="text-xs font-medium text-emerald-700 hover:text-emerald-900 px-2.5 py-1 rounded border border-emerald-200 hover:bg-emerald-50"
                      data-testid={`missing-dates-wa-${u.id}`}
                    >
                      WhatsApp
                    </a>
                    <button
                      onClick={() => handleEditUser(u)}
                      className="text-xs font-medium text-[#5B6FD6] hover:text-[#4338CA] px-2.5 py-1 rounded border border-[#5B6FD6]/30 hover:bg-[#5B6FD6]/5"
                      data-testid={`missing-dates-edit-${u.id}`}
                    >
                      Add dates
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pending Approvals */}
        {pendingUsers.length > 0 && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl overflow-hidden" data-testid="pending-approvals-section">
            <div className="px-5 py-3 bg-amber-100/60 border-b border-amber-200 flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-700">Pending Approvals ({pendingUsers.length})</span>
              <span className="text-xs text-amber-500 ml-2">New logins require Super Admin approval</span>
            </div>
            {pendingUsers.map(user => (
              <div key={user.id} className="flex items-center justify-between px-5 py-3 border-b border-amber-100 last:border-0" data-testid={`pending-user-${user.id}`}>
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                    background: user.role === 'admin'
                      ? 'linear-gradient(135deg, #5B6FD6, #4338CA)'
                      : 'linear-gradient(135deg, #94a3b8, #64748b)'
                  }}>
                    {user.full_name?.charAt(0)}
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-900">{user.full_name}</span>
                    <span className="text-xs text-slate-400 ml-2">{user.mobile_number}</span>
                  </div>
                  <Badge className={roleConfig[user.role]?.color || 'bg-slate-200 text-slate-600'} variant="secondary">
                    {roleConfig[user.role]?.label || user.role}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 h-8 px-4 text-xs"
                    onClick={() => handleToggleStatus(user.id, false)} data-testid={`approve-user-${user.id}`}>
                    <UserCheck className="h-3 w-3 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 px-3 text-xs text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => handleDeleteClick(user)} data-testid={`reject-user-${user.id}`}>
                    <Trash2 className="h-3 w-3 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Organization Tree */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="org-tree">
          {/* Header */}
          <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600 uppercase tracking-wider items-center">
            <div className="col-span-2">Name</div>
            <div className="col-span-2">Phone</div>
            <div className="col-span-1">Password</div>
            <div className="col-span-1">Role</div>
            <div className="col-span-1">Reports To</div>
            <div className="col-span-2">Backup</div>
            <div className="col-span-1">Leave</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {/* Super Admins (with any directly-reporting members) */}
          {superAdmins.map(sa => {
            const directMembers = getMembersForSuperAdmin(sa.id);
            const isExpanded = expandedAdmins.has(sa.id);
            return (
              <div key={sa.id}>
                {directMembers.length > 0 ? (
                  <div className="flex items-center cursor-pointer"
                    onClick={() => toggleExpand(sa.id)} data-testid={`sa-row-${sa.id}`}>
                    <button className="pl-3 pr-1 py-3 text-slate-400 hover:text-slate-600" data-testid={`expand-sa-${sa.id}`}>
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    <div className="flex-1" onClick={(e) => e.stopPropagation()}>
                      <UserRow user={sa} />
                    </div>
                  </div>
                ) : (
                  <UserRow user={sa} />
                )}
                {isExpanded && directMembers.length > 0 && (
                  <div className="border-t border-slate-100/50">
                    {directMembers.map(member => (
                      <div key={member.id} className="border-t border-slate-100/50">
                        <UserRow user={member} isChild />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Admins with their Members */}
          {admins.map(admin => {
            const adminMembers = getMembersForAdmin(admin.id);
            const isExpanded = expandedAdmins.has(admin.id);
            return (
              <div key={admin.id}>
                <div className="flex items-center border-t border-slate-100 cursor-pointer"
                  onClick={() => toggleExpand(admin.id)} data-testid={`admin-row-${admin.id}`}>
                  <button className="pl-3 pr-1 py-3 text-slate-400 hover:text-slate-600" data-testid={`expand-admin-${admin.id}`}>
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  <div className="flex-1" onClick={(e) => e.stopPropagation()}>
                    <UserRow user={admin} />
                  </div>
                </div>
                {isExpanded && adminMembers.length > 0 && (
                  <div className="border-t border-slate-100/50">
                    {adminMembers.map(member => (
                      <div key={member.id} className="border-t border-slate-100/50">
                        <UserRow user={member} isChild />
                      </div>
                    ))}
                  </div>
                )}
                {isExpanded && adminMembers.length === 0 && (
                  <div className="pl-14 py-3 text-xs text-slate-400 italic bg-slate-50/30 border-t border-slate-100/50">
                    No members assigned
                  </div>
                )}
              </div>
            );
          })}

          {/* Unassigned Members */}
          {unassignedMembers.length > 0 && (
            <>
              <div className="px-5 py-2.5 bg-amber-50 border-t border-amber-200 text-xs font-semibold text-amber-700 uppercase tracking-wider flex items-center gap-2">
                <ArrowRight className="h-3.5 w-3.5" /> Unassigned Members ({unassignedMembers.length})
              </div>
              {unassignedMembers.map(member => (
                <div key={member.id} className="border-t border-slate-100">
                  <UserRow user={member} />
                </div>
              ))}
            </>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent data-testid="delete-user-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete User</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{userToDelete?.full_name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-user">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reset Password Dialog */}
        <Dialog open={!!resetPwdUserId} onOpenChange={() => setResetPwdUserId(null)}>
          <DialogContent className="max-w-sm" data-testid="reset-password-dialog">
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>Set a new password for {users.find(u => u.id === resetPwdUserId)?.full_name}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>New Password <span className="text-red-500">*</span></Label>
                <Input type="text" value={resetPwdValue} onChange={e => setResetPwdValue(e.target.value)} placeholder="Enter new password (min 6 chars)" data-testid="reset-pwd-input" />
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t">
                <Button variant="outline" onClick={() => setResetPwdUserId(null)}>Cancel</Button>
                <Button className="bg-[#5B6FD6] hover:bg-[#4a5bc2]" onClick={handleResetPassword} disabled={!resetPwdValue || resetPwdValue.length < 6} data-testid="submit-reset-pwd">Reset Password</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Wipe All Tasks Dialog (Super Admin only) */}
        <Dialog open={wipeDialogOpen} onOpenChange={(open) => { if (!open) { setWipeDialogOpen(false); setWipeConfirmText(''); } }}>
          <DialogContent className="max-w-md" data-testid="wipe-tasks-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                Wipe ALL Tasks?
              </DialogTitle>
              <DialogDescription className="text-slate-700 pt-2">
                This will permanently delete <strong>every task</strong> in the database, plus
                all associated <strong>time logs, notifications, and activity history</strong>.
                Clients, services, users, and holidays will be preserved.
                <br /><br />
                <span className="text-red-700 font-semibold">This action is irreversible.</span>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-sm">Type <code className="px-1.5 py-0.5 bg-slate-100 rounded text-red-600 text-xs">DELETE ALL TASKS</code> to confirm:</Label>
                <Input
                  value={wipeConfirmText}
                  onChange={(e) => setWipeConfirmText(e.target.value)}
                  placeholder="DELETE ALL TASKS"
                  data-testid="wipe-confirm-input"
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t">
                <Button variant="outline" onClick={() => { setWipeDialogOpen(false); setWipeConfirmText(''); }} disabled={wiping}>Cancel</Button>
                <Button
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="wipe-submit"
                  disabled={wipeConfirmText !== 'DELETE ALL TASKS' || wiping}
                  onClick={async () => {
                    setWiping(true);
                    try {
                      const res = await api.post('/admin/wipe-tasks', { confirmation: 'DELETE ALL TASKS' });
                      const d = res.data?.deleted || {};
                      toast.success(`Wiped: ${d.tasks ?? 0} tasks, ${d.notifications ?? 0} notifications, ${d.activity_logs ?? 0} activity entries.`);
                      setWipeDialogOpen(false);
                      setWipeConfirmText('');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to wipe tasks');
                    } finally {
                      setWiping(false);
                    }
                  }}
                >
                  {wiping ? 'Wiping…' : 'Wipe All Tasks'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Exit Employee Wizard */}
        <ExitEmployeeDialog
          open={!!exitDialogUser}
          onOpenChange={(o) => { if (!o) setExitDialogUser(null); }}
          user={exitDialogUser}
          allUsers={users}
          onExited={fetchUsers}
        />

        {/* Approve Password Reset Dialog */}
        <Dialog open={!!approveTarget} onOpenChange={(o) => { if (!o) { setApproveTarget(null); setApproveNewPwd(''); } }}>
          <DialogContent className="max-w-sm" data-testid="approve-reset-dialog">
            <DialogHeader>
              <DialogTitle>Approve Password Reset</DialogTitle>
              <DialogDescription>
                Set a new temporary password for <b>{approveTarget?.user_name}</b> ({approveTarget?.user_mobile}).
                After approval the password will be visible in the table below until the user logs in once.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <div>
                <Label htmlFor="approve_new_pwd">New Password</Label>
                <Input id="approve_new_pwd" type="password" placeholder="Minimum 6 characters"
                  value={approveNewPwd}
                  onChange={(e) => setApproveNewPwd(e.target.value)}
                  data-testid="approve-new-password-input" />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => { setApproveTarget(null); setApproveNewPwd(''); }} data-testid="cancel-approve-btn">Cancel</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleApproveReset}
                  disabled={!approveNewPwd || approveNewPwd.length < 6}
                  data-testid="submit-approve-reset-btn">
                  Approve & Set Password
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
    </div>
  );
}
